import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { parseSunfireCsv, SunfireRow } from '../services/csvParser';
import { parseAudioFilename, autoMatch, uploadToS3, MatchedPair, AudioFile } from '../services/uploadService';
import { api } from '../services/api';
import { useDownloadCart } from '../services/downloadCart';
import { getAudioDuration } from '../services/audioConverter';

type Step = 'csv' | 'audio' | 'match' | 'upload';

interface UploadStatus {
  confirmationNumber: string;
  // 'transcoding' = uploaded successfully, server is converting to MP3 in the background.
  stage: 'pending' | 'creating' | 'uploading' | 'confirming' | 'transcoding' | 'done' | 'error';
  progress: number;
  error?: string;
}

export default function UploadCalls() {
  const navigate = useNavigate();
  const { addItems } = useDownloadCart();

  // System state
  const [systemId, setSystemId] = useState('dialedin');

  // Step state
  const [step, setStep] = useState<Step>('csv');

  // CSV state
  const [csvRows, setCsvRows] = useState<SunfireRow[]>([]);
  const [editingCell, setEditingCell] = useState<{ row: number; field: keyof SunfireRow } | null>(null);
  const [editValue, setEditValue] = useState('');
  const csvInputRef = useRef<HTMLInputElement>(null);

  // Audio state
  const [audioFiles, setAudioFiles] = useState<AudioFile[]>([]);
  const audioInputRef = useRef<HTMLInputElement>(null);

  // Match state
  const [matchedPairs, setMatchedPairs] = useState<MatchedPair[]>([]);
  const [unmatchedCsv, setUnmatchedCsv] = useState<SunfireRow[]>([]);
  const [unmatchedAudio, setUnmatchedAudio] = useState<AudioFile[]>([]);
  const [selectedAudio, setSelectedAudio] = useState<number | null>(null);

  // Upload state
  const [uploadStatuses, setUploadStatuses] = useState<UploadStatus[]>([]);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [ingestResults, setIngestResults] = useState<any[]>([]);
  // Set on unmount so the upload/transcode polling loops stop and no
  // setState fires after the user navigates away.
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return () => { cancelledRef.current = true; };
  }, []);

  // ==================== CSV HANDLING ====================

  const handleCsvUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const rows = parseSunfireCsv(text);
      setCsvRows(rows);
    };
    reader.readAsText(file);
  };

  const startEdit = (rowIdx: number, field: keyof SunfireRow) => {
    setEditingCell({ row: rowIdx, field });
    setEditValue(csvRows[rowIdx][field]);
  };

  const commitEdit = () => {
    if (!editingCell) return;
    setCsvRows(prev => {
      const updated = [...prev];
      updated[editingCell.row] = { ...updated[editingCell.row], [editingCell.field]: editValue };
      return updated;
    });
    setEditingCell(null);
  };

  const handleEditKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') commitEdit();
    if (e.key === 'Escape') setEditingCell(null);
  };

  // ==================== AUDIO HANDLING ====================

  const handleAudioUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const parsed: AudioFile[] = files.map(f => {
      const { phone, date } = parseAudioFilename(f.name);
      return { file: f, phone, date };
    });
    setAudioFiles(prev => [...prev, ...parsed]);
  };

  const handleCsvDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = Array.from(e.dataTransfer.files).find(f => /\.csv$/i.test(f.name));
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const rows = parseSunfireCsv(text);
      setCsvRows(rows);
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter(f =>
      /\.(mp3|wav|m4a|ogg|flac|aac)$/i.test(f.name)
    );
    const parsed: AudioFile[] = files.map(f => {
      const { phone, date } = parseAudioFilename(f.name);
      return { file: f, phone, date };
    });
    setAudioFiles(prev => [...prev, ...parsed]);
  }, []);

  // ==================== MATCHING ====================

  const runAutoMatch = () => {
    const result = autoMatch(csvRows, audioFiles);
    setMatchedPairs(result.matched);
    setUnmatchedCsv(result.unmatchedCsv);
    setUnmatchedAudio(result.unmatchedAudio);
    // If everything matched cleanly there's nothing to review or fix manually,
    // so skip Step 3 and go straight to upload. Step 3 still appears for the
    // 1-in-20 case where filenames don't carry phone+date and need manual pairing.
    const allMatched = result.unmatchedCsv.length === 0
      && result.unmatchedAudio.length === 0
      && result.matched.length > 0;
    if (allMatched) {
      // Pass the matches directly — React state setters above haven't flushed yet.
      startUpload(result.matched);
    } else {
      setStep('match');
    }
  };

  const manualMatch = (csvIdx: number) => {
    if (selectedAudio === null) return;
    const audio = unmatchedAudio[selectedAudio];
    const csv = unmatchedCsv[csvIdx];
    setMatchedPairs(prev => [...prev, { csvRow: csv, audioFile: audio.file, matchType: 'manual' }]);
    setUnmatchedCsv(prev => prev.filter((_, i) => i !== csvIdx));
    setUnmatchedAudio(prev => prev.filter((_, i) => i !== selectedAudio));
    setSelectedAudio(null);
  };

  const unmatch = (idx: number) => {
    const pair = matchedPairs[idx];
    const audio: AudioFile = { file: pair.audioFile, ...parseAudioFilename(pair.audioFile.name) };
    setMatchedPairs(prev => prev.filter((_, i) => i !== idx));
    setUnmatchedCsv(prev => [...prev, pair.csvRow]);
    setUnmatchedAudio(prev => [...prev, audio]);
  };

  // ==================== UPLOAD ====================

  const startUpload = async (pairsOverride?: MatchedPair[]) => {
    // Allow auto-skip (Step 2 → Step 4) to pass pairs directly, since
    // setMatchedPairs() in the same handler hasn't flushed by the time we read it.
    const pairs = pairsOverride ?? matchedPairs;
    if (pairsOverride) setMatchedPairs(pairsOverride);
    setStep('upload');
    const statuses: UploadStatus[] = pairs.map(p => ({
      confirmationNumber: p.csvRow.confirmationNumber,
      stage: 'pending' as const,
      progress: 0,
    }));
    setUploadStatuses(statuses);

    // Step 1: Create DynamoDB records + get pre-signed URLs
    const updateStatus = (idx: number, update: Partial<UploadStatus>) => {
      if (cancelledRef.current) return; // unmounted — drop the update
      setUploadStatuses(prev => {
        const next = [...prev];
        next[idx] = { ...next[idx], ...update };
        return next;
      });
    };

    pairs.forEach((_, i) => updateStatus(i, { stage: 'creating' }));

    // Forward each file's actual MIME type so the backend can route non-MP3 files
    // through the server-side transcoder (ffmpeg → 96k mono MP3) instead of asking
    // the browser to do a fragile, memory-bound WAV decode.
    const ingestItems = pairs.map(p => ({
      confirmationNumber: p.csvRow.confirmationNumber,
      phone: p.csvRow.applicantPhone,
      date: p.csvRow.date,
      time: p.csvRow.time,
      agentId: p.csvRow.agentId,
      agentName: p.csvRow.agentName,
      agentEmail: p.csvRow.agentEmail,
      beneficiaryName: `${p.csvRow.applicantFirstName} ${p.csvRow.applicantLastName}`.trim(),
      audioContentType: p.audioFile.type || guessContentType(p.audioFile.name),
      systemId,
    }));

    const reportFailure = (
      pk: string | undefined,
      sk: string | undefined,
      stage: 'ingest' | 'converting' | 'uploading' | 'confirming' | 'unknown',
      error: string,
      file?: File,
    ) => {
      if (!pk || !sk) return;
      api.reportUploadFailure({
        PK: pk, SK: sk, stage, error,
        fileSizeBytes: file?.size,
        fileName: file?.name,
      }).catch(() => { /* logged server-side anyway */ });
    };

    let results: any[];
    try {
      const resp = await api.uploadIngestBatch(ingestItems);
      results = resp.results;
      setIngestResults(results);
    } catch (err: any) {
      pairs.forEach((_, i) => updateStatus(i, { stage: 'error', error: err.message }));
      return;
    }

    results.forEach((r: any, i: number) => {
      if (r.error) updateStatus(i, { stage: 'error', error: r.error });
    });

    // Capture MP3 duration locally where we can (browser <audio> element). For
    // non-MP3 files the server extracts duration during transcoding.
    const uploadQueue = results
      .map((r: any, i: number) => ({ result: r, index: i }))
      .filter((q: any) => !q.result.error);

    for (const { result: r, index: i } of uploadQueue) {
      if (r.tempUploadKey) continue; // server handles duration for non-MP3
      try {
        const dur = await getAudioDuration(pairs[i].audioFile);
        if (results[i]) results[i].duration = dur;
      } catch { /* duration is best-effort */ }
    }

    // Upload original file straight to whichever S3 key the backend picked.
    // 3 concurrent. No conversion in the browser — even WAVs go up raw.
    const concurrency = 3;
    let queueIdx = 0;
    const uploadedIndices = new Set<number>();

    const processUpload = async () => {
      while (queueIdx < uploadQueue.length) {
        if (cancelledRef.current) return; // navigated away — stop uploading
        const current = uploadQueue[queueIdx++];
        if (!current) break;
        const { result: r, index: i } = current;
        const fileToUpload = pairs[i].audioFile;

        try {
          updateStatus(i, { stage: 'uploading', progress: 0 });
          await uploadToS3(r.uploadUrl, fileToUpload, (pct) => {
            updateStatus(i, { progress: pct });
          });
          uploadedIndices.add(i);
          updateStatus(i, { stage: 'confirming', progress: 100 });
        } catch (err: any) {
          updateStatus(i, { stage: 'error', error: err.message });
          reportFailure(r.PK, r.SK, 'uploading', err.message, fileToUpload);
        }
      }
    };

    const workers = Array.from({ length: Math.min(concurrency, uploadQueue.length) }, () => processUpload());
    await Promise.all(workers);

    const toConfirm = results
      .map((r: any, i: number) => ({ ...r, index: i }))
      .filter((r: any) => r.s3Key && !r.error && uploadedIndices.has(r.index));

    if (toConfirm.length > 0) {
      try {
        const confirmResp = await api.uploadConfirmBatch(
          toConfirm.map((r: any) => ({
            recordingId: r.recordingId,
            s3Key: r.s3Key,
            PK: r.PK,
            SK: r.SK,
            duration: r.duration || 0,
            tempUploadKey: r.tempUploadKey || null,
            sourceContentType: r.sourceContentType,
          }))
        );

        confirmResp.results.forEach((cr: any, ci: number) => {
          const idx = toConfirm[ci].index;
          if (cr.status === 'confirmed') {
            updateStatus(idx, { stage: 'done' });
          } else if (cr.status === 'transcoding') {
            // File is in S3, server is converting in the background.
            updateStatus(idx, { stage: 'transcoding', progress: 100 });
          } else {
            updateStatus(idx, { stage: 'error', error: cr.error || 'Confirm failed' });
            reportFailure(toConfirm[ci].PK, toConfirm[ci].SK, 'confirming', cr.error || 'Confirm failed', pairs[idx].audioFile);
          }
        });
      } catch (err: any) {
        toConfirm.forEach((item: any) => {
          updateStatus(item.index, { stage: 'error', error: err.message });
          reportFailure(item.PK, item.SK, 'confirming', err.message, pairs[item.index].audioFile);
        });
      }
    }

    // Poll the backend for any rows still in 'transcoding'. The transcoder Lambda
    // updates DDB with transcodeProgress (percent) every ~3s and ultimately flips
    // storageStatus to s3_confirmed (or upload_failed). 5 min cap so we never poll
    // forever on a hung row — admins still see those in the Quarantine tab.
    const transcodingItems = toConfirm
      .filter((r: any, ci: number) => {
        // Read from the current results array since updateStatus is async.
        // Cleaner: rely on the confirmResp branch we just took — only items with
        // status='transcoding' need polling. We already set their stage above.
        return r.tempUploadKey;
      })
      .map((r: any) => ({ PK: r.PK, SK: r.SK, index: r.index }));

    if (transcodingItems.length > 0) {
      const startedAt = Date.now();
      const POLL_INTERVAL_MS = 3000;
      const POLL_TIMEOUT_MS = 5 * 60 * 1000;
      const remaining = new Map(transcodingItems.map((t: any) => [`${t.PK}|${t.SK}`, t.index]));

      while (remaining.size > 0 && Date.now() - startedAt < POLL_TIMEOUT_MS) {
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
        if (cancelledRef.current) return; // navigated away — stop polling
        try {
          const items = Array.from(remaining.entries()).map(([key, _idx]) => {
            const [PK, SK] = key.split('|');
            return { PK, SK };
          });
          const resp = await api.getUploadProgress(items);
          for (const row of resp.items || []) {
            const key = `${row.PK}|${row.SK}`;
            const idx = remaining.get(key);
            if (idx === undefined) continue;
            if (row.status === 's3_confirmed' || row.status === 'fully_archived') {
              updateStatus(idx, { stage: 'done' });
              remaining.delete(key);
            } else if (row.status === 'upload_failed') {
              updateStatus(idx, { stage: 'error', error: row.failureError || 'Server-side conversion failed' });
              remaining.delete(key);
            } else if (row.status === 'transcoding') {
              if (typeof row.percent === 'number') {
                updateStatus(idx, { progress: row.percent });
              }
            }
          }
        } catch { /* network blip — try again next interval */ }
      }
      // Anything still left after the timeout: leave stuck. The Quarantine tab
      // surfaces these (transcoder Lambda has already either finished or hit its
      // own 5-min Lambda timeout, which itself flips the row to upload_failed).
    }

    if (cancelledRef.current) return;
    setUploadComplete(true);
  };

  // Best-effort MIME guess from extension when the browser doesn't supply file.type.
  const guessContentType = (filename: string): string => {
    const m = filename.toLowerCase().match(/\.([a-z0-9]+)$/);
    const ext = m?.[1] || '';
    const map: Record<string, string> = {
      mp3: 'audio/mpeg', wav: 'audio/wav', m4a: 'audio/mp4',
      ogg: 'audio/ogg', flac: 'audio/flac', aac: 'audio/aac',
    };
    return map[ext] || 'application/octet-stream';
  };

  const addToCart = () => {
    const cartItems = ingestResults
      .filter((r: any) => !r.error)
      .map((r: any, i: number) => {
        const pair = matchedPairs[i];
        return {
          recordingId: `${r.PK}|${r.SK}`,
          agentId: pair.csvRow.agentId || '',
          callerId: pair.csvRow.applicantPhone?.slice(-4) || '',
          recordDate: pair.csvRow.date || '',
          systemId,
          duration: r.duration ? `${Math.floor(r.duration / 60)}:${(r.duration % 60).toString().padStart(2, '0')}` : undefined,
          hasTranscript: false,
          originalFilename: r.normalizedFilename || '',
        };
      });

    const added = addItems(cartItems);
    if (added > 0) {
      navigate('/download-cart');
    }
  };

  // ==================== FORMATTING ====================

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  // ==================== RENDER ====================

  const stepIndicator = (
    <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
      {(['csv', 'audio', 'match', 'upload'] as Step[]).map((s, i) => {
        const labels = ['1. Import CSV', '2. Upload Audio', '3. Match', '4. Upload & Ingest'];
        const isActive = s === step;
        const isPast = ['csv', 'audio', 'match', 'upload'].indexOf(step) > i;
        return (
          <div key={s} style={{
            flex: 1, padding: '10px 12px', borderRadius: 8, textAlign: 'center', fontSize: 13, fontWeight: isActive ? 600 : 400,
            background: isActive ? '#e0f7fa' : isPast ? '#e8f5e9' : '#f5f5f5',
            color: isActive ? '#00838f' : isPast ? '#2e7d32' : '#999',
            border: isActive ? '2px solid #00bcd4' : '2px solid transparent',
          }}>
            {labels[i]}
          </div>
        );
      })}
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <h1>Upload Calls</h1>
        <p>Import enrollment CSV, match audio files, and ingest into the archive</p>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-heading)' }}>System:</label>
        <select
          value={systemId}
          onChange={e => setSystemId(e.target.value)}
          disabled={step === 'upload'}
          style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, background: '#fff' }}
        >
          <option value="dialedin">DialedIn (Medicare)</option>
          <option value="pulsedial">PulseDial</option>
          <option value="vicidial">Vicidial</option>
          <option value="3cx-legacy">3CX Legacy</option>
        </select>
      </div>

      {stepIndicator}

      {/* ==================== STEP 1: CSV ==================== */}
      {step === 'csv' && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e8e8e8' }}>
          <h3 style={{ margin: '0 0 16px 0', color: 'var(--text-heading)' }}>Import Sunfire Enrollment CSV</h3>

          {csvRows.length === 0 ? (
            <div
              onClick={() => csvInputRef.current?.click()}
              onDrop={handleCsvDrop}
              onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#00bcd4'; }}
              onDragLeave={e => { e.currentTarget.style.borderColor = '#ccc'; }}
              style={{
                border: '2px dashed #ccc', borderRadius: 12, padding: '48px 24px', textAlign: 'center',
                cursor: 'pointer', transition: 'border-color 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#00bcd4'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#ccc'; }}
            >
              <div style={{ fontSize: 36, marginBottom: 12 }}>CSV</div>
              <div style={{ fontSize: 15, color: '#555', marginBottom: 8 }}>Drag and drop a Sunfire enrollment CSV here, or click to browse</div>
              <div style={{ fontSize: 12, color: '#999' }}>Supported: .csv files</div>
              <input ref={csvInputRef} type="file" accept=".csv" onChange={handleCsvUpload} style={{ display: 'none' }} />
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 14, color: '#555' }}>
                  <strong>{csvRows.length}</strong> enrollment{csvRows.length !== 1 ? 's' : ''} loaded
                  {csvRows.filter(r => !r.applicantPhone).length > 0 && (
                    <span style={{ color: '#e65100', marginLeft: 8 }}>
                      ({csvRows.filter(r => !r.applicantPhone).length} missing phone)
                    </span>
                  )}
                </span>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => { setCsvRows([]); if (csvInputRef.current) csvInputRef.current.value = ''; }}
                    style={{ padding: '6px 14px', border: '1px solid #ddd', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13 }}>
                    Clear
                  </button>
                  <button onClick={() => setStep('audio')}
                    style={{ padding: '6px 14px', border: 'none', borderRadius: 6, background: 'var(--brand-cyan)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                    Next: Upload Audio
                  </button>
                </div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table className="data-table" style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th>Confirmation #</th>
                      <th>Date</th>
                      <th>Phone</th>
                      <th>Name</th>
                      <th>Agent</th>
                      <th>Agent Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvRows.map((row, i) => (
                      <tr key={row.confirmationNumber}>
                        <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{row.confirmationNumber}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{row.submitDate}</td>
                        <td
                          onClick={() => startEdit(i, 'applicantPhone')}
                          style={{
                            cursor: 'pointer', fontFamily: 'monospace',
                            background: !row.applicantPhone ? '#fff3e0' : 'transparent',
                          }}
                        >
                          {editingCell?.row === i && editingCell?.field === 'applicantPhone' ? (
                            <input
                              autoFocus
                              value={editValue}
                              onChange={e => setEditValue(e.target.value)}
                              onBlur={commitEdit}
                              onKeyDown={handleEditKeyDown}
                              style={{ width: '100%', padding: '2px 4px', border: '1px solid #00bcd4', borderRadius: 3, fontSize: 12, fontFamily: 'monospace' }}
                            />
                          ) : (
                            row.applicantPhone || <span style={{ color: '#e65100', fontStyle: 'italic' }}>click to add</span>
                          )}
                        </td>
                        <td
                          onClick={() => startEdit(i, 'applicantFirstName')}
                          style={{ cursor: 'pointer' }}
                        >
                          {editingCell?.row === i && editingCell?.field === 'applicantFirstName' ? (
                            <input
                              autoFocus
                              value={editValue}
                              onChange={e => setEditValue(e.target.value)}
                              onBlur={commitEdit}
                              onKeyDown={handleEditKeyDown}
                              style={{ width: '100%', padding: '2px 4px', border: '1px solid #00bcd4', borderRadius: 3, fontSize: 12 }}
                            />
                          ) : (
                            `${row.applicantFirstName} ${row.applicantLastName}`
                          )}
                        </td>
                        <td
                          onClick={() => startEdit(i, 'agentName')}
                          style={{ cursor: 'pointer' }}
                        >
                          {editingCell?.row === i && editingCell?.field === 'agentName' ? (
                            <input
                              autoFocus
                              value={editValue}
                              onChange={e => setEditValue(e.target.value)}
                              onBlur={commitEdit}
                              onKeyDown={handleEditKeyDown}
                              style={{ width: '100%', padding: '2px 4px', border: '1px solid #00bcd4', borderRadius: 3, fontSize: 12 }}
                            />
                          ) : (
                            row.agentName
                          )}
                        </td>
                        <td style={{ fontSize: 11, color: '#666' }}>{row.agentEmail}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* ==================== STEP 2: AUDIO ==================== */}
      {step === 'audio' && (() => {
        // Compute live matches as files are added
        const matchedCsvIndices = new Set<number>();
        const usedAudioIndices = new Set<number>();
        for (let ai = 0; ai < audioFiles.length; ai++) {
          const af = audioFiles[ai];
          if (!af.phone || usedAudioIndices.has(ai)) continue;
          for (let ci = 0; ci < csvRows.length; ci++) {
            if (matchedCsvIndices.has(ci)) continue;
            const row = csvRows[ci];
            if (row.applicantPhone === af.phone && (!af.date || row.date === af.date)) {
              matchedCsvIndices.add(ci);
              usedAudioIndices.add(ai);
              break;
            }
          }
        }
        const loadedCount = matchedCsvIndices.size;

        return (
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e8e8e8' }}>
          <h3 style={{ margin: '0 0 16px 0', color: 'var(--text-heading)' }}>Upload Audio Files</h3>

          <div
            onDrop={e => { e.currentTarget.style.borderColor = '#ccc'; handleDrop(e); }}
            onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = '#00bcd4'; }}
            onDragLeave={e => { e.currentTarget.style.borderColor = '#ccc'; }}
            onClick={() => audioInputRef.current?.click()}
            style={{
              border: '2px dashed #ccc', borderRadius: 12, padding: '28px 24px', textAlign: 'center',
              cursor: 'pointer', transition: 'border-color 0.2s', marginBottom: 16,
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = '#00bcd4'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#ccc'; }}
          >
            <div style={{ fontSize: 15, color: '#555', marginBottom: 4 }}>Drag and drop audio files here, or click to browse</div>
            <div style={{ fontSize: 12, color: '#999' }}>
              Name files like <code style={{ background: '#e8e8e8', padding: '2px 4px', borderRadius: 3 }}>3308034565_20260330.mp3</code> for auto-matching
            </div>
            <input ref={audioInputRef} type="file" accept=".mp3,.wav,.m4a,.ogg,.flac,.aac" multiple onChange={handleAudioUpload} style={{ display: 'none' }} />
          </div>

          {/* Unified checklist — CSV rows with match status */}
          {csvRows.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 13, color: '#555' }}>
                  <strong style={{ color: loadedCount === csvRows.length ? '#2e7d32' : 'var(--text-heading)' }}>
                    {loadedCount} / {csvRows.length}
                  </strong> calls matched
                </span>
                {audioFiles.length > 0 && audioFiles.length > usedAudioIndices.size && (
                  <span style={{ fontSize: 12, color: '#e65100' }}>
                    {audioFiles.length - usedAudioIndices.size} file{audioFiles.length - usedAudioIndices.size !== 1 ? 's' : ''} unmatched
                  </span>
                )}
              </div>
              <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #e8e8e8', borderRadius: 8 }}>
                <table className="data-table" style={{ fontSize: 11, marginBottom: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}></th>
                      <th>Phone</th>
                      <th>Name</th>
                      <th>Date</th>
                      <th>Agent Email</th>
                      <th>Audio File</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csvRows.map((row, ci) => {
                      const isMatched = matchedCsvIndices.has(ci);
                      const matchedFile = isMatched
                        ? audioFiles.find((af, ai) => {
                            if (!af.phone) return false;
                            return row.applicantPhone === af.phone && (!af.date || row.date === af.date);
                          })
                        : null;
                      return (
                        <tr key={ci} style={{ background: isMatched ? '#f1f8e9' : undefined }}>
                          <td style={{ textAlign: 'center', fontSize: 16 }}>
                            {isMatched
                              ? <span style={{ color: '#2e7d32' }}>&#10003;</span>
                              : <span style={{ color: '#ddd' }}>&#9675;</span>
                            }
                          </td>
                          <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.applicantPhone || '—'}</td>
                          <td>{row.applicantFirstName} {row.applicantLastName}</td>
                          <td style={{ whiteSpace: 'nowrap' }}>{row.submitDate}</td>
                          <td style={{ fontSize: 10, color: '#666' }}>{row.agentEmail}</td>
                          <td style={{ fontFamily: 'monospace', fontSize: 10, color: isMatched ? '#2e7d32' : '#ccc' }}>
                            {matchedFile ? matchedFile.file.name : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
            <button onClick={() => setStep('csv')}
              style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13 }}>
              Back
            </button>
            <button onClick={runAutoMatch} disabled={audioFiles.length === 0}
              style={{
                padding: '8px 16px', border: 'none', borderRadius: 6, cursor: audioFiles.length > 0 ? 'pointer' : 'not-allowed',
                background: audioFiles.length > 0 ? 'var(--brand-cyan)' : '#ccc', color: '#fff', fontSize: 13, fontWeight: 600,
              }}>
              Next: Match Files
            </button>
          </div>
        </div>
        );
      })()}

      {/* ==================== STEP 3: MATCH ==================== */}
      {step === 'match' && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e8e8e8' }}>
          <h3 style={{ margin: '0 0 8px 0', color: 'var(--text-heading)' }}>Match Audio Files to Enrollments</h3>
          <div style={{ fontSize: 13, color: '#555', marginBottom: 16 }}>
            <span style={{ color: '#2e7d32', fontWeight: 600 }}>{matchedPairs.length} matched</span>
            {unmatchedCsv.length > 0 && <span style={{ color: '#e65100', marginLeft: 12 }}>{unmatchedCsv.length} unmatched enrollments</span>}
            {unmatchedAudio.length > 0 && <span style={{ color: '#e65100', marginLeft: 12 }}>{unmatchedAudio.length} unmatched audio files</span>}
          </div>

          {/* Matched pairs */}
          {matchedPairs.length > 0 && (
            <>
              <h4 style={{ margin: '0 0 8px 0', fontSize: 13, color: '#2e7d32' }}>Matched Pairs</h4>
              <table className="data-table" style={{ fontSize: 12, marginBottom: 20 }}>
                <thead>
                  <tr>
                    <th>Confirmation #</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Audio File</th>
                    <th>Match</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {matchedPairs.map((pair, i) => (
                    <tr key={i}>
                      <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{pair.csvRow.confirmationNumber}</td>
                      <td>{pair.csvRow.applicantFirstName} {pair.csvRow.applicantLastName}</td>
                      <td style={{ fontFamily: 'monospace' }}>{pair.csvRow.applicantPhone}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{pair.audioFile.name}</td>
                      <td>
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                          background: pair.matchType === 'auto' ? '#e8f5e9' : '#e3f2fd',
                          color: pair.matchType === 'auto' ? '#2e7d32' : '#1565c0',
                        }}>
                          {pair.matchType === 'auto' ? 'AUTO' : 'MANUAL'}
                        </span>
                      </td>
                      <td>
                        <button onClick={() => unmatch(i)}
                          style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: 12 }}>
                          unmatch
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {/* Unmatched — manual matching UI */}
          {(unmatchedCsv.length > 0 || unmatchedAudio.length > 0) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              {/* Unmatched audio files (select first) */}
              <div>
                <h4 style={{ margin: '0 0 8px 0', fontSize: 13, color: '#e65100' }}>
                  Unmatched Audio ({unmatchedAudio.length})
                  {selectedAudio !== null && <span style={{ color: '#00838f', marginLeft: 8 }}>— now click an enrollment to pair</span>}
                </h4>
                {unmatchedAudio.map((af, i) => (
                  <div key={i}
                    onClick={() => setSelectedAudio(selectedAudio === i ? null : i)}
                    style={{
                      padding: '8px 12px', marginBottom: 4, borderRadius: 6, cursor: 'pointer', fontSize: 12,
                      border: selectedAudio === i ? '2px solid #00bcd4' : '1px solid #e8e8e8',
                      background: selectedAudio === i ? '#e0f7fa' : '#fff',
                    }}>
                    <div style={{ fontFamily: 'monospace', fontSize: 11 }}>{af.file.name}</div>
                    <div style={{ fontSize: 10, color: '#999' }}>{formatFileSize(af.file.size)}</div>
                  </div>
                ))}
                {unmatchedAudio.length === 0 && (
                  <div style={{ fontSize: 12, color: '#999', fontStyle: 'italic' }}>All audio files matched</div>
                )}
              </div>

              {/* Unmatched CSV rows (click to pair with selected audio) */}
              <div>
                <h4 style={{ margin: '0 0 8px 0', fontSize: 13, color: '#e65100' }}>
                  Unmatched Enrollments ({unmatchedCsv.length})
                </h4>
                {unmatchedCsv.map((row, i) => (
                  <div key={i}
                    onClick={() => selectedAudio !== null && manualMatch(i)}
                    style={{
                      padding: '8px 12px', marginBottom: 4, borderRadius: 6, fontSize: 12,
                      border: '1px solid #e8e8e8', background: '#fff',
                      cursor: selectedAudio !== null ? 'pointer' : 'default',
                      opacity: selectedAudio !== null ? 1 : 0.6,
                    }}
                    onMouseEnter={e => { if (selectedAudio !== null) e.currentTarget.style.background = '#e0f7fa'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
                  >
                    <div><strong>{row.applicantFirstName} {row.applicantLastName}</strong> — {row.confirmationNumber}</div>
                    <div style={{ fontSize: 11, color: '#666' }}>
                      {row.applicantPhone || 'no phone'} | {row.submitDate} | {row.agentName}
                    </div>
                  </div>
                ))}
                {unmatchedCsv.length === 0 && (
                  <div style={{ fontSize: 12, color: '#999', fontStyle: 'italic' }}>All enrollments matched</div>
                )}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between' }}>
            <button onClick={() => setStep('audio')}
              style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13 }}>
              Back
            </button>
            <button onClick={() => startUpload()} disabled={matchedPairs.length === 0}
              style={{
                padding: '8px 16px', border: 'none', borderRadius: 6,
                cursor: matchedPairs.length > 0 ? 'pointer' : 'not-allowed',
                background: matchedPairs.length > 0 ? '#2e7d32' : '#ccc', color: '#fff', fontSize: 13, fontWeight: 600,
              }}>
              Upload {matchedPairs.length} Matched Pair{matchedPairs.length !== 1 ? 's' : ''}
            </button>
          </div>
        </div>
      )}

      {/* ==================== STEP 4: UPLOAD ==================== */}
      {step === 'upload' && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 24, border: '1px solid #e8e8e8' }}>
          <h3 style={{ margin: '0 0 16px 0', color: 'var(--text-heading)' }}>
            {uploadComplete ? 'Upload Complete' : 'Uploading...'}
          </h3>

          {/* Overall progress — count transcoding rows as "uploaded" (server takes it from here). */}
          {!uploadComplete && (() => {
            const completed = uploadStatuses.filter(s => s.stage === 'done' || s.stage === 'transcoding').length;
            return (
              <div style={{ marginBottom: 20 }}>
                <div style={{ height: 6, background: '#e8e8e8', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: 3, transition: 'width 0.3s',
                    background: 'var(--brand-cyan)',
                    width: `${(completed / uploadStatuses.length) * 100}%`,
                  }} />
                </div>
                <div style={{ fontSize: 12, color: '#999', marginTop: 4 }}>
                  {completed} / {uploadStatuses.length} uploaded
                </div>
              </div>
            );
          })()}

          <table className="data-table" style={{ fontSize: 12, marginBottom: 20 }}>
            <thead>
              <tr>
                <th>Confirmation #</th>
                <th>Name</th>
                <th>Status</th>
                <th>Progress</th>
              </tr>
            </thead>
            <tbody>
              {uploadStatuses.map((status, i) => {
                const pair = matchedPairs[i];
                const stageLabels: Record<string, string> = {
                  pending: 'Waiting',
                  creating: 'Creating record...',
                  uploading: 'Uploading to S3...',
                  confirming: 'Confirming...',
                  transcoding: status.progress > 0
                    ? `Converting on server (${status.progress}%)`
                    : 'Uploaded — converting on server',
                  done: 'Done',
                  error: 'Failed',
                };
                const stageColors: Record<string, string> = {
                  pending: '#999',
                  creating: '#f39c12',
                  uploading: '#00bcd4',
                  confirming: '#f39c12',
                  transcoding: '#9c27b0',
                  done: '#2e7d32',
                  error: '#e74c3c',
                };
                return (
                  <tr key={i}>
                    <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{status.confirmationNumber}</td>
                    <td>{pair?.csvRow.applicantFirstName} {pair?.csvRow.applicantLastName}</td>
                    <td>
                      <span style={{ color: stageColors[status.stage], fontWeight: 600, fontSize: 11 }}>
                        {stageLabels[status.stage]}
                      </span>
                      {status.error && <div style={{ fontSize: 10, color: '#e74c3c' }}>{status.error}</div>}
                    </td>
                    <td style={{ width: 120 }}>
                      {(status.stage === 'uploading' || status.stage === 'transcoding') && (
                        <div style={{ height: 4, background: '#e8e8e8', borderRadius: 2, overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', borderRadius: 2, transition: 'width 0.2s',
                            background: status.stage === 'transcoding' ? '#9c27b0' : '#00bcd4',
                            width: `${status.progress}%`,
                          }} />
                        </div>
                      )}
                      {status.stage === 'done' && <span style={{ color: '#2e7d32' }}>100%</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {uploadComplete && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => {
                setStep('csv');
                setCsvRows([]);
                setAudioFiles([]);
                setMatchedPairs([]);
                setUnmatchedCsv([]);
                setUnmatchedAudio([]);
                setUploadStatuses([]);
                setUploadComplete(false);
                setIngestResults([]);
              }}
                style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 13 }}>
                Upload More
              </button>
              <button onClick={addToCart}
                style={{ padding: '8px 16px', border: 'none', borderRadius: 6, background: 'var(--brand-cyan)', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Add to Download Cart
              </button>
              <button onClick={() => navigate('/export-sms')}
                style={{ padding: '8px 16px', border: 'none', borderRadius: 6, background: '#27ae60', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                Export to SMS
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
